import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { LoadingLine, PageBody, useNow } from '@wundo/ui';
import { PageTitle } from '@/components/PageTitle.tsx';
import { TeamColleagueList } from '@/components/team/TeamColleagueList.tsx';
import { TeamEmptyCard } from '@/components/team/TeamEmptyCard.tsx';
import { TeamIntroBanner } from '@/components/team/TeamIntroBanner.tsx';
import { TeamSummary } from '@/components/team/TeamSummary.tsx';
import { TeamToolbar } from '@/components/team/TeamToolbar.tsx';
import { DEFAULT_TEAM_SORT, type TeamSort } from '@/components/team/sortColleagues.ts';
import type { TeamFilter } from '@/components/team/teamFilter.ts';
import { useColleagueRelationship } from '@/components/team/useColleagueRelationship.ts';
import { useOneOnOneSupport } from '@/components/team/useOneOnOneSupport.ts';
import { useTeamColleagues } from '@/components/team/useTeamColleagues.ts';
import { usePublishChatPageOffer } from '@/lib/chatPageOffer.ts';
import { useChatPageNudge } from '@/lib/useChatPageNudge.ts';
import { useConnectedCalendars } from '@/lib/useConnectedCalendars.ts';
import { m } from '@/paraglide/messages.js';

export const Route = createFileRoute('/_app/team/')({
  component: TeamPage,
});

/* Where the drawer beside this page is standing, for the nudge it asks for. */
const NUDGE_PAGE = { page: 'team' } as const;

/**
 * The colleagues the user has 1:1s with, a card each, and for each whether
 * Wundo prepares those 1:1s automatically. The switch is the same person
 * follow as an onboarding pick; the list is the one source for the cards, the
 * filter counts and the card on top. See docs/design/team.md.
 */
export function TeamPage() {
  const nudge = useChatPageNudge(NUDGE_PAGE);
  usePublishChatPageOffer({
    quickActions: [
      { text: m.chat_quick_team_longest_ago(), icon: 'recap' },
      { text: m.chat_quick_team_attention_split(), icon: 'people' },
    ],
    nudge,
  });

  const [filter, setFilter] = useState<TeamFilter>('all');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<TeamSort>(DEFAULT_TEAM_SORT);
  const now = useNow();
  const { colleagues, content } = useTeamColleagues({ filter, search, sort, now });
  const { noCalendar } = useConnectedCalendars();

  const support = useOneOnOneSupport();
  const relationshipChange = useColleagueRelationship();

  return (
    <>
      <PageTitle>{m.nav_team()}</PageTitle>
      <PageBody>
        {content.kind === 'loading' && <LoadingLine label={m.team_loading()} />}
        {content.kind === 'failed' && (
          <p className='text-base text-danger'>{m.team_failed()}</p>
        )}
        {content.kind === 'empty' && <TeamEmptyCard noCalendar={noCalendar} />}

        {content.kind === 'overview' && (
          <>
            {colleagues.length > 0 && (
              <>
                <TeamSummary
                  colleagues={colleagues}
                  onPickGroup={(group) =>
                    setFilter(group === 'memory' ? 'memory' : 'no_memory')
                  }
                />
                <TeamIntroBanner />
              </>
            )}

            <TeamToolbar
              colleagues={colleagues}
              now={now}
              search={search}
              onSearchChange={setSearch}
              filter={filter}
              onFilterChange={setFilter}
              sort={sort}
              onSortChange={setSort}
            />
            {(support.isError || relationshipChange.isError) && (
              <p role='alert' className='mt-3 text-sm text-danger'>
                {m.team_support_failed()}
              </p>
            )}

            <TeamColleagueList
              list={content.list}
              filter={filter}
              onSupportChange={(colleague, supported) =>
                support.mutate({ colleague, supported })
              }
              onRelationshipChange={(colleague, relationship) =>
                relationshipChange.mutate({ colleague, relationship })
              }
            />
          </>
        )}
      </PageBody>
    </>
  );
}
